/**
 * Shared contract between the API (which appends the param to links in
 * outbound SMS) and the web app (which reads it at bootstrap and remembers it
 * as the visitor's sign-in identifier).
 *
 * Kept deliberately short — every character costs SMS segment budget, and these
 * links are already close to the 160-char limit.
 */
export const SMS_RECIPIENT_PHONE_PARAM = 'p';

/**
 * The ONLY shape the API ever emits: ten national digits, optionally carrying a
 * leading US country code.
 */
const RECIPIENT_PHONE_SHAPE = /^1?\d{10}$/;

/**
 * The recipient phone from a `?p=` param as bare national digits, or null when
 * the value isn't one we could have emitted.
 *
 * Deliberately validates the WHOLE string rather than stripping non-digits
 * first: `?p=` arrives from a URL anyone can author or forward, and a
 * strip-then-check would happily accept `abc5558675309`, a 2000-digit value, or
 * the `"1,2"` that API Gateway produces from a repeated `?p=1&p=2`. Consumers
 * persist this as a sign-in identifier and pre-fill it into auth forms, so an
 * unrecognized value must be dropped, not coerced into something plausible.
 * @param value - Raw `?p=` value from a URL.
 * @returns Ten bare digits, or null.
 */
export function normalizeSmsRecipientPhone(
  value?: string | null,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !RECIPIENT_PHONE_SHAPE.test(trimmed)) {
    return null;
  }

  return trimmed.length === 11 ? trimmed.slice(1) : trimmed;
}
